import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { userThemesService } from '../../../services/userThemesService';
import { SaveToast, useSaveToast } from './SaveToast';

const darkenHex = (hex, amount = 40) => {
  const h = (hex || '#0e7572').replace('#', '');
  const num = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount);
  const b = Math.max(0, (num & 0xff) - amount);
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
};

const configToPreview = (config) => ({
  bg:     config?.backgroundColor  || config?.backgroundType === 'solid' ? (config?.backgroundColor || '#ffffff') : '#ffffff',
  accent: config?.accentColor      || '#0e7572',
  input:  config?.inputBackground  || '#ffffff',
  label:  config?.labelColor       || '#111827',
  border: config?.accentColor      || '#0e7572',
});

const THEMES = [
  {
    id: 'modern-minimalist',
    name: 'Modern Minimalist',
    description: 'Clean lines, generous whitespace, subtle borders',
    icon: 'Minimize2',
    preview: {
      bg: '#fafafa',
      accent: '#18181b',
      input: '#ffffff',
      label: '#18181b',
      border: '#e4e4e7',
    },
    config: {
      fontFamily: 'DM Sans',
      fontSize: '14',
      fontWeight: '400',
      labelFontWeight: '500',
      labelColor: '#18181b',
      accentColor: '#18181b',
      inputBackground: '#ffffff',
      inputTextColor: '#18181b',
      borderRadius: '4',
      backgroundColor: '#fafafa',
      backgroundType: 'solid',
      footerColor: '#09090b',
    },
  },
  {
    id: 'corporate-clean',
    name: 'Corporate Clean',
    description: 'Professional, structured, trust-inspiring layout',
    icon: 'Briefcase',
    preview: {
      bg: '#f8fafc',
      accent: '#0f4c81',
      input: '#ffffff',
      label: '#1e293b',
      border: '#cbd5e1',
    },
    config: {
      fontFamily: 'Plus Jakarta Sans',
      fontSize: '14',
      fontWeight: '400',
      labelFontWeight: '600',
      labelColor: '#1e293b',
      accentColor: '#0f4c81',
      inputBackground: '#ffffff',
      inputTextColor: '#1e293b',
      borderRadius: '6',
      backgroundColor: '#f8fafc',
      backgroundType: 'solid',
      footerColor: '#082741',
    },
  },
  {
    id: 'dark-mode',
    name: 'Dark Mode',
    description: 'Sleek dark surfaces with vibrant accent highlights',
    icon: 'Moon',
    preview: {
      bg: '#0f172a',
      accent: '#6366f1',
      input: '#1e293b',
      label: '#f1f5f9',
      border: '#334155',
    },
    config: {
      fontFamily: 'Manrope',
      fontSize: '14',
      fontWeight: '400',
      labelFontWeight: '500',
      labelColor: '#f1f5f9',
      accentColor: '#6366f1',
      inputBackground: '#1e293b',
      inputTextColor: '#f1f5f9',
      borderRadius: '8',
      backgroundColor: '#0f172a',
      backgroundType: 'solid',
      footerColor: '#0c0e2e',
    },
  },
  {
    id: 'warm-pastel',
    name: 'Warm Pastel',
    description: 'Soft, friendly tones with a welcoming feel',
    icon: 'Sunset',
    preview: {
      bg: '#fffbf5',
      accent: '#d97706',
      input: '#ffffff',
      label: '#78350f',
      border: '#fde68a',
    },
    config: {
      fontFamily: 'General Sans',
      fontSize: '14',
      fontWeight: '400',
      labelFontWeight: '500',
      labelColor: '#78350f',
      accentColor: '#d97706',
      inputBackground: '#ffffff',
      inputTextColor: '#451a03',
      borderRadius: '12',
      backgroundColor: '#fffbf5',
      backgroundType: 'solid',
      footerColor: '#92400e',
    },
  },
  {
    id: 'ocean-breeze',
    name: 'Ocean Breeze',
    description: 'Cool teal gradient with a fresh, airy aesthetic',
    icon: 'Waves',
    preview: {
      bg: '#f0fdfa',
      accent: '#0d9488',
      input: '#ffffff',
      label: '#134e4a',
      border: '#99f6e4',
    },
    config: {
      fontFamily: 'DM Sans',
      fontSize: '14',
      fontWeight: '400',
      labelFontWeight: '500',
      labelColor: '#134e4a',
      accentColor: '#0d9488',
      inputBackground: '#ffffff',
      inputTextColor: '#134e4a',
      borderRadius: '10',
      backgroundType: 'gradient',
      gradientColor1: '#f0fdfa',
      gradientColor2: '#ccfbf1',
      gradientDirection: 'to bottom right',
      footerColor: '#065f46',
    },
  },
  {
    id: 'bold-editorial',
    name: 'Bold Editorial',
    description: 'High contrast, strong typography, editorial feel',
    icon: 'BookOpen',
    preview: {
      bg: '#ffffff',
      accent: '#dc2626',
      input: '#f9fafb',
      label: '#111827',
      border: '#111827',
    },
    config: {
      fontFamily: 'Satoshi',
      fontSize: '15',
      fontWeight: '400',
      labelFontWeight: '700',
      labelColor: '#111827',
      accentColor: '#dc2626',
      inputBackground: '#f9fafb',
      inputTextColor: '#111827',
      borderRadius: '0',
      backgroundColor: '#ffffff',
      backgroundType: 'solid',
      footerColor: '#7f1d1d',
    },
  },
];

// ─── Built-in theme preview card ─────────────────────────────────────────────
const ThemePreviewCard = ({ theme, isActive, onApply, isFooterBarActive }) => {
  const { preview } = theme;
  const [showFooterHex, setShowFooterHex] = useState(false);
  const footerBarColor = theme?.config?.footerColor || darkenHex(theme?.config?.accentColor || preview?.accent || '#0e7572', 40);
  return (
    <div
      className={`rounded-xl border-2 overflow-hidden cursor-pointer transition-all hover:shadow-md ${
        isActive ? 'border-primary shadow-md' : 'border-border hover:border-primary/40'
      }`}
      onClick={() => onApply(theme)}
    >
      {/* Mini form preview */}
      <div className="p-3 relative" style={{ backgroundColor: preview?.bg }}>
        <div className="space-y-2">
          <div className="text-[9px] font-semibold" style={{ color: preview?.label }}>Full Name</div>
          <div
            className="h-5 rounded flex items-center px-2"
            style={{
              backgroundColor: preview?.input,
              border: `1px solid ${preview?.border}`,
              borderRadius: `${theme?.config?.borderRadius || 6}px`,
            }}
          >
            <span className="text-[8px]" style={{ color: preview?.label, opacity: 0.4 }}>Enter your name...</span>
          </div>
          <div className="text-[9px] font-semibold mt-1" style={{ color: preview?.label }}>Email</div>
          <div
            className="h-5 rounded flex items-center px-2"
            style={{
              backgroundColor: preview?.input,
              border: `1px solid ${preview?.border}`,
              borderRadius: `${theme?.config?.borderRadius || 6}px`,
            }}
          >
            <span className="text-[8px]" style={{ color: preview?.label, opacity: 0.4 }}>your@email.com</span>
          </div>
          <div
            className="mt-2 h-6 rounded flex items-center justify-center"
            style={{ backgroundColor: preview?.accent, borderRadius: `${theme?.config?.borderRadius || 6}px` }}
          >
            <span className="text-[9px] font-semibold text-white">Submit</span>
          </div>
        </div>
        {isActive && (
          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
            <Icon name="Check" size={10} color="white" />
          </div>
        )}
      </div>
      {/* Footer bar strip */}
      {isFooterBarActive ? (
        <div
          className="h-4 flex items-center justify-between px-2 relative cursor-default"
          style={{ backgroundColor: footerBarColor }}
          onMouseEnter={() => setShowFooterHex(true)}
          onMouseLeave={() => setShowFooterHex(false)}
          title={`Footer bar colour: ${footerBarColor}`}
        >
          <p className="text-[7px] font-medium opacity-60" style={{ color: '#ffffff' }}>Footer bar</p>
          {showFooterHex && (
            <span className="text-[7px] font-mono font-semibold opacity-90 tracking-wide" style={{ color: '#ffffff' }}>
              {footerBarColor}
            </span>
          )}
        </div>
      ) : (
        <div className="h-4 flex items-center justify-center px-2 bg-muted/40 border-t border-border/40">
          <p className="text-[7px] text-muted-foreground opacity-70 italic">Only visible with Footer Bar progress style</p>
        </div>
      )}
      {/* Theme info */}
      <div className="px-3 py-2 bg-card border-t border-border">
        <div className="flex items-center gap-1.5 mb-0.5">
          <Icon name={theme?.icon || 'Bookmark'} size={12} color="var(--color-primary)" />
          <span className="text-xs font-semibold text-foreground">{theme?.name}</span>
        </div>
        <p className="text-[10px] text-muted-foreground leading-tight">{theme?.description}</p>
      </div>
    </div>
  );
};

// ─── User-saved theme card ────────────────────────────────────────────────────
const UserThemeCard = ({ theme, isActive, isDefault, onApply, onRename, onDelete, onSetDefault, onClearDefault, isFooterBarActive }) => {
  const { preview } = theme;
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(theme?.name);
  const footerBarColor = theme?.config?.footerColor || darkenHex(theme?.config?.accentColor || preview?.accent || '#0e7572', 40);

  const commitRename = () => {
    const trimmed = editName?.trim();
    if (trimmed && trimmed !== theme?.name) onRename(theme?.id, trimmed);
    setEditing(false);
  };

  return (
    <div className={`rounded-xl border-2 overflow-hidden transition-all hover:shadow-md ${
      isActive ? 'border-primary shadow-md' : 'border-border hover:border-primary/40'
    }`}>
      {/* Mini preview — clickable to apply */}
      <div className="p-3 relative cursor-pointer" style={{ backgroundColor: preview?.bg }} onClick={() => onApply(theme)}>
        <div className="space-y-2">
          <div className="text-[9px] font-semibold" style={{ color: preview?.label }}>Full Name</div>
          <div
            className="h-5 rounded flex items-center px-2"
            style={{
              backgroundColor: preview?.input,
              border: `1px solid ${preview?.border}`,
              borderRadius: `${theme?.config?.borderRadius || 6}px`,
            }}
          >
            <span className="text-[8px]" style={{ color: preview?.label, opacity: 0.4 }}>Enter your name...</span>
          </div>
          <div
            className="mt-2 h-6 rounded flex items-center justify-center"
            style={{ backgroundColor: preview?.accent, borderRadius: `${theme?.config?.borderRadius || 6}px` }}
          >
            <span className="text-[9px] font-semibold text-white">Submit</span>
          </div>
        </div>
        {isActive && (
          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
            <Icon name="Check" size={10} color="white" />
          </div>
        )}
      </div>
      {/* Footer strip */}
      {isFooterBarActive ? (
        <div className="h-4 flex items-center px-2" style={{ backgroundColor: footerBarColor }}>
          <p className="text-[7px] font-medium opacity-60" style={{ color: '#ffffff' }}>Footer bar</p>
        </div>
      ) : (
        <div className="h-4 flex items-center justify-center px-2 bg-muted/40 border-t border-border/40">
          <p className="text-[7px] text-muted-foreground opacity-70 italic">Only visible with Footer Bar style</p>
        </div>
      )}
      {/* Theme info + controls */}
      <div className="px-3 py-2 bg-card border-t border-border space-y-1.5">
        {editing ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              value={editName}
              onChange={e => setEditName(e?.target?.value)}
              onKeyDown={e => { if (e?.key === 'Enter') commitRename(); if (e?.key === 'Escape') setEditing(false); }}
              className="flex-1 text-xs border border-primary rounded px-1.5 py-0.5 focus:outline-none"
            />
            <button onClick={commitRename} className="text-primary hover:text-primary/80">
              <Icon name="Check" size={12} />
            </button>
            <button onClick={() => setEditing(false)} className="text-muted-foreground hover:text-foreground">
              <Icon name="X" size={12} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <Icon name="Bookmark" size={12} color="var(--color-primary)" />
            <span className="text-xs font-semibold text-foreground flex-1 truncate">{theme?.name}</span>
            <button
              onClick={() => { setEditName(theme?.name); setEditing(true); }}
              className="text-muted-foreground hover:text-primary transition-colors p-0.5"
              title="Rename"
            >
              <Icon name="Pencil" size={11} />
            </button>
            <button
              onClick={() => onDelete(theme?.id)}
              className="text-muted-foreground hover:text-destructive transition-colors p-0.5"
              title="Delete"
            >
              <Icon name="Trash2" size={11} />
            </button>
          </div>
        )}
        {/* Set as default row */}
        {isDefault ? (
          <div className="flex items-center gap-1.5">
            <Icon name="Star" size={10} color="var(--color-primary)" />
            <span className="text-[10px] font-medium text-primary flex-1">Default for new forms</span>
            <button
              onClick={() => onClearDefault(theme?.id)}
              className="text-[10px] text-muted-foreground hover:text-destructive transition-colors"
              title="Remove default"
            >
              Remove
            </button>
          </div>
        ) : (
          <button
            onClick={() => onSetDefault(theme?.id)}
            className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-primary transition-colors"
            title="Set as default for new forms"
          >
            <Icon name="Star" size={10} />
            Set as default
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PrebuiltThemes = ({ globalTheme = {}, onUpdateTheme, onApplyTheme, multiStepConfig }) => {
  const [activeThemeId, setActiveThemeId] = useState(globalTheme?._themeId || null);
  const [defaultThemeId, setDefaultThemeId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);
  const [userThemes, setUserThemes] = useState([]);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const { toasts, addToast, dismissToast } = useSaveToast(2500);
  const isFooterBarActive = multiStepConfig?.enabled && multiStepConfig?.progressStyle === 'footer-bar';

  useEffect(() => {
    loadUserThemes();
  }, []);

  const loadUserThemes = async () => {
    try {
      const [rows, defaultRow] = await Promise.all([
        userThemesService?.getThemes(),
        userThemesService?.getDefaultTheme(),
      ]);
      setUserThemes(rows?.map(rowToTheme));
      setDefaultThemeId(defaultRow?.id || null);
    } catch (_) {}
  };

  const rowToTheme = (row) => ({
    id: row?.id,
    name: row?.name,
    description: 'Your saved theme',
    icon: 'Bookmark',
    isUserTheme: true,
    preview: configToPreview(row?.config || {}),
    config: row?.config || {},
  });

  const showFeedback = (msg, type = 'success') => {
    addToast(msg, type);
  };

  const handleSave = async () => {
    const name = saveName?.trim();
    if (!name) return;
    setSaving(true);
    try {
      // Strip internal _themeId so saved themes start clean
      const { _themeId, ...themeConfig } = globalTheme;
      const row = await userThemesService?.saveTheme(name, themeConfig);
      const newTheme = rowToTheme(row);
      setUserThemes(prev => [newTheme, ...prev]);
      setSaveName('');
      setShowSaveInput(false);
      // Associate the current form with the newly saved theme and persist immediately
      const appliedConfig = { ...themeConfig, _themeId: row?.id };
      onApplyTheme ? onApplyTheme(appliedConfig) : onUpdateTheme?.(appliedConfig);
      setActiveThemeId(row?.id);
      showFeedback(`"${name}" saved`);
    } catch (err) {
      showFeedback('Failed to save theme', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      // If this theme is the current default, clear the persisted preference
      // first so new forms don't resolve a dangling reference.
      if (defaultThemeId === id) {
        await userThemesService?.clearDefaultTheme();
        setDefaultThemeId(null);
      }
      await userThemesService?.deleteTheme(id);
      setUserThemes(prev => prev?.filter(t => t?.id !== id));
      if (activeThemeId === id) setActiveThemeId(null);
      showFeedback('Theme deleted');
    } catch (_) {
      showFeedback('Failed to delete theme', 'error');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await userThemesService?.setDefaultTheme(id);
      setDefaultThemeId(id);
      showFeedback('Default theme set — new forms will use this theme');
    } catch (_) {
      showFeedback('Failed to set default theme', 'error');
    }
  };

  const handleClearDefault = async (id) => {
    try {
      await userThemesService?.clearDefaultTheme();
      setDefaultThemeId(null);
      showFeedback('Default theme removed');
    } catch (_) {
      showFeedback('Failed to remove default theme', 'error');
    }
  };

  const handleRename = async (id, name) => {
    try {
      await userThemesService?.updateThemeName(id, name);
      setUserThemes(prev => prev?.map(t => t?.id === id ? { ...t, name } : t));
    } catch (_) {
      showFeedback('Failed to rename theme', 'error');
    }
  };

  const handleApply = (theme) => {
    setShowConfirm(theme);
  };

  const confirmApply = () => {
    if (!showConfirm) return;
    const config = { ...showConfirm?.config, _themeId: showConfirm?.id };
    if (!showConfirm?.config?.footerColor && globalTheme?.footerColor) {
      config.footerColor = globalTheme.footerColor;
    }
    // Use onApplyTheme when available so the change is immediately persisted
    onApplyTheme ? onApplyTheme(config) : onUpdateTheme?.(config);
    setActiveThemeId(showConfirm?.id);
    setShowConfirm(null);
  };

  return (
    <div className="space-y-5">
      <SaveToast toasts={toasts} onDismiss={dismissToast} />

      {/* Save current as theme */}
      <div className="p-3 border border-dashed border-border rounded-lg bg-muted/20">
        {showSaveInput ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-foreground">Name your theme</p>
            <div className="flex gap-2">
              <input
                autoFocus
                type="text"
                value={saveName}
                onChange={e => setSaveName(e?.target?.value)}
                onKeyDown={e => { if (e?.key === 'Enter') handleSave(); if (e?.key === 'Escape') { setShowSaveInput(false); setSaveName(''); } }}
                placeholder="e.g. My Brand Theme"
                className="flex-1 text-xs border border-input rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={handleSave}
                disabled={saving || !saveName?.trim()}
                className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => { setShowSaveInput(false); setSaveName(''); }}
                className="px-3 py-1.5 text-xs font-medium border border-border rounded-md hover:bg-muted transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowSaveInput(true)}
            className="w-full flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground hover:text-primary transition-colors py-1"
          >
            <Icon name="BookmarkPlus" size={14} />
            Save current styling as a theme
          </button>
        )}
      </div>

      {/* User-saved themes */}
      {userThemes?.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Icon name="Bookmark" size={13} color="var(--color-primary)" />
            <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">Your Themes</h4>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {userThemes?.map(theme => (
              <UserThemeCard
                key={theme?.id}
                theme={theme}
                isActive={activeThemeId === theme?.id}
                isDefault={defaultThemeId === theme?.id}
                onApply={handleApply}
                onRename={handleRename}
                onDelete={handleDelete}
                onSetDefault={handleSetDefault}
                onClearDefault={handleClearDefault}
                isFooterBarActive={isFooterBarActive}
              />
            ))}
          </div>
        </div>
      )}

      {/* Built-in themes */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Icon name="Sparkles" size={13} color="var(--color-primary)" />
          <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">Built-in Themes</h4>
        </div>
        {/* Info banner */}
        <div className="flex items-start gap-2.5 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <Icon name="Info" size={14} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-muted-foreground">
            Applying a theme overwrites your current Global Theme settings. You can fine-tune afterwards.
          </p>
        </div>
        {/* Active theme badge */}
        {activeThemeId && THEMES?.find(t => t?.id === activeThemeId) && (
          <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 rounded-lg border border-primary/30">
            <Icon name="CheckCircle2" size={14} color="var(--color-primary)" />
            <span className="text-xs font-medium text-primary">
              Active: {THEMES?.find(t => t?.id === activeThemeId)?.name}
            </span>
            <button
              onClick={() => { onUpdateTheme?.({}); setActiveThemeId(null); }}
              className="ml-auto text-[10px] text-muted-foreground hover:text-destructive transition-colors"
            >
              Clear
            </button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          {THEMES?.map(theme => (
            <ThemePreviewCard
              key={theme?.id}
              theme={theme}
              isActive={activeThemeId === theme?.id}
              onApply={handleApply}
              isFooterBarActive={isFooterBarActive}
            />
          ))}
        </div>
      </div>

      {/* Confirm apply modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-card rounded-xl border border-border shadow-xl p-5 w-72 mx-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="AlertTriangle" size={18} color="var(--color-warning, #f59e0b)" />
              <h3 className="text-sm font-semibold text-foreground">Apply Theme?</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Applying <strong>{showConfirm?.name}</strong> will replace your current global theme settings. This cannot be undone automatically.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowConfirm(null)}
                className="flex-1 py-2 text-xs font-medium border border-border rounded-lg hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmApply}
                className="flex-1 py-2 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Apply Theme
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrebuiltThemes;
